import * as React from 'react';
import { DataGrid, Typography } from '@SBGSports/referee-react';
import { ColDef } from 'ag-grid-community';
import { useGetDeepActivity } from 'api';
import { CellDropdownSelectProps } from 'focus/component/focus-table/cell-dropdown-select';
import { CellDropdownSelectRenderer } from 'focus/component/focus-table/cell-dropdown-select-renderer';
import { useTranslations } from 'i18n';
import { useMemo, useRef, useState, useEffect } from 'react';
import { calcTableHeight } from 'reporting/helpers';
import { useFocusImport } from '../use-focus-import';
import styles from '../focus-import.module.css';
import { useFocusStoredOptions } from '../use-focus-stored-options';
import { TableSearch } from '../component/table-search';
import mixpanel from 'mixpanel-browser';
import { EVENTS } from 'analytics/analytics-events';
import { useFocusImportSelections } from 'focus/use-focus-import-selections';
import { DEFAULT_TABLE_COLUMN_DEF } from '../../reporting/constants';

interface AthleteParticipationByFocusLabelsProps {
    uniqueFocusLabels: string[];
    onValueChanged: (athleteAssignment: Map<string, string>) => void;
}

export const AthleteParticipationByFocusLabels: React.FC<AthleteParticipationByFocusLabelsProps> = (props) => {
    const { onValueChanged, uniqueFocusLabels } = props;
    //Hooks
    const { activityId } = useFocusImport();
    const { data: deepActivity } = useGetDeepActivity(activityId);
    const { __ } = useTranslations();
    const { focusStoredOptions, setFocusStoredOptions } = useFocusStoredOptions();
    const { selectionsRef } = useFocusImportSelections();

    //States
    const [searchText, setSearchText] = useState<string>();

    //Sync between persistence and available options, then set athlete->label selections in selectionsRef so its available in the provider after first render
    const initialDataSet = useRef(false);
    useEffect(() => {
        if (initialDataSet.current) {
            return;
        }

        const initialSelection = new Map<string, string>();
        deepActivity?.[0]?.athletes?.forEach((athlete) => {
            const assignment = focusStoredOptions.labelSelectedAthleteMap?.get(athlete.id ?? '');
            if (assignment && uniqueFocusLabels?.includes(assignment)) {
                initialSelection.set(athlete.id ?? '', assignment);
            }
        });
        onValueChanged(initialSelection);
        initialDataSet.current = true;
    }, [deepActivity, focusStoredOptions.labelSelectedAthleteMap, onValueChanged, uniqueFocusLabels]);

    const rowData = useMemo(() => {
        return (
            uniqueFocusLabels?.map((label) => {
                const options =
                    deepActivity?.[0]?.athletes?.map((athlete) => {
                        const assignment = focusStoredOptions.labelSelectedAthleteMap?.get(athlete.id ?? '');
                        return {
                            value: athlete.name,
                            id: athlete.id,
                            isSelected: assignment === label,
                            isDisabled:
                                focusStoredOptions.labelSelectedAthleteMap?.has(athlete.id ?? '') &&
                                uniqueFocusLabels.includes(assignment ?? '') &&
                                assignment !== label,
                        };
                    }) ?? [];

                return {
                    focus_labels: label,
                    select: {
                        testId: label,
                        options: options,
                        onChange: (option) => {
                            const newMap = selectionsRef.current.athleteAssignment ?? new Map();
                            if (option?.id !== undefined) {
                                newMap.set(String(option.id), label);
                            }

                            newMap.forEach((value, key) => {
                                if (key !== option?.id && value === label) {
                                    newMap.delete(key);
                                }
                            });
                            setFocusStoredOptions({
                                ...focusStoredOptions,
                                labelSelectedAthleteMap: new Map([...newMap]),
                            });
                            mixpanel.track(EVENTS.focus.athleteParticipationByLabelSelection, {
                                labelSelectedAthleteMap: new Map([...newMap]),
                            });
                            onValueChanged(new Map([...newMap]));
                        },
                    } as CellDropdownSelectProps,
                };
            }) ?? []
        );
    }, [deepActivity, focusStoredOptions, onValueChanged, selectionsRef, setFocusStoredOptions, uniqueFocusLabels]);

    const COLUMN_DEF: ColDef[] = [
        {
            flex: 1,
            headerName: __('smt_importer.focus_labels'),
            field: 'focus_labels',
            sortable: true,
        },
        {
            flex: 1,
            headerName: __('smt_importer.openfield_athletes'),
            field: 'openfield_athletes',
            cellRenderer: CellDropdownSelectRenderer,
        },
    ];
    return (
        <div data-testid="athlete-participation-by-focus-labels" className={styles['page-body']}>
            <div className={styles['search-and-count']}>
                <TableSearch searchText={searchText ?? ''} setSearchText={setSearchText} />
                <Typography variant="caption-1">
                    {__('smt_importer.count_of_total_assigned', {
                        count: selectionsRef.current.athleteAssignment?.size?.toLocaleString(),
                        total: uniqueFocusLabels?.length?.toLocaleString(),
                    })}
                </Typography>
            </div>
            <div style={{ height: `${calcTableHeight(rowData)}` }}>
                <DataGrid
                    testId="focus-athlete-participation-by-label-table"
                    defaultColDef={DEFAULT_TABLE_COLUMN_DEF}
                    columnDefs={COLUMN_DEF}
                    rowData={rowData}
                    gridOptions={{
                        getRowStyle: () => ({ backgroundColor: 'var(--r-catapult-white)' }),
                        overlayNoRowsTemplate: `${__('misc.no_data_available')}`,
                    }}
                    dense
                    quickFilterText={searchText}
                />
            </div>
        </div>
    );
};
